import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, RadioButtonUnchecked, TimelapseOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  firstIncompleteMyCompanyPage,
  myCompanyPageChecks,
  myCompanyPageProgressStatus,
  type MyCompanyPageId,
  type MyCompanyProgressStatus,
} from '../utils/myCompanyRequired';
import { focusMyCompanyMissing, myCompanyMissingHasFocusTarget } from '../utils/myCompanyFocus';

type MyCompanyRequiredNoticeProps = {
  profile: unknown;
  pageId?: MyCompanyPageId;
  /** When true on a section page, show a saving caption in the instruction card. */
  saving?: boolean;
};

const NEXT_ACTION: Record<MyCompanyPageId, string> = {
  company:
    'Save company essentials (name and GST). UDYAM or CIN is required to save identity details. Then choose and save your export preference.',
  energy:
    'Fill primary energy source, main fuels, and electricity supplier when you use grid power, then save energy details.',
  units:
    'Name each manufacturing unit (not “Unit 1”) and list its products, then save.',
  scope1:
    'Choose direct emission sources (or confirm none), then save direct emissions so the review timestamp is stored.',
  scope3:
    'Keep at least one value-chain category, say what stays outside, then save value chain.',
  boundaries:
    'Complete the organizational boundary questions (consolidation, entity description, fuel/fleet/logistics), then save.',
};

const REQUIRED_SUMMARY: Record<MyCompanyPageId, string> = {
  company:
    'For workspace unlock: company name, GST, and export activity (yes or no). Saving essentials identity also needs UDYAM or CIN.',
  energy: 'Primary energy source, main fuels, and electricity supplier for grid/electric sources.',
  units: 'A real manufacturing unit name and products for each unit.',
  scope1: 'Direct emission sources (or none) and a successful save (review timestamp).',
  scope3: 'Value-chain categories, out-of-boundary note, and a successful save (review timestamp).',
  boundaries: 'Organizational boundary fields with no remaining validation issues.',
};

const statusLabel = (status: MyCompanyProgressStatus): string => {
  if (status === 'complete') return 'Complete';
  if (status === 'in_progress') return 'In progress';
  return 'Not started';
};

const MyCompanyRequiredNotice: React.FC<MyCompanyRequiredNoticeProps> = ({
  profile,
  pageId,
  saving = false,
}) => {
  const typedProfile = profile as Parameters<typeof myCompanyPageChecks>[0];
  const checks = myCompanyPageChecks(typedProfile);
  const completeCount = checks.filter((page) => page.complete).length;
  const progressPct = Math.round((completeCount / checks.length) * 100);
  const current = pageId ? checks.find((page) => page.id === pageId) : null;
  const nextIncomplete = firstIncompleteMyCompanyPage(typedProfile);

  if (pageId && current) {
    const sectionStatus = myCompanyPageProgressStatus(typedProfile, current.id);
    if (current.complete) {
      return (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="my-company-section-complete">
          {current.label} is complete. You can still edit and save updates.
        </Alert>
      );
    }

    const attention = sectionStatus === 'in_progress' ? 'Needs attention' : 'Not started';

    return (
      <Card variant="outlined" sx={{ mb: 2 }} data-testid="my-company-setup-instruction">
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Still needed on this page
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="my-company-section-status"
              >
                {saving ? 'Saving…' : attention}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Required: {REQUIRED_SUMMARY[current.id]}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {current.missing.map((item) => (
                <Typography key={item} component="li" variant="body2" sx={{ mb: 0.25 }}>
                  {myCompanyMissingHasFocusTarget(item) ? (
                    <Button
                      type="button"
                      variant="text"
                      color="success"
                      size="small"
                      onClick={() => focusMyCompanyMissing(item)}
                      data-testid="my-company-focus-missing"
                      sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        verticalAlign: 'baseline',
                        fontWeight: 600,
                        color: 'success.dark',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      {item}
                    </Button>
                  ) : (
                    item
                  )}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Next: {NEXT_ACTION[current.id]}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Home checklist only while setup remains incomplete (persisted profile via myCompanyPageChecks).
  if (completeCount === checks.length) {
    return null;
  }

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2 }}
      data-testid="my-company-home-progress"
      aria-label="My company setup progress"
    >
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
          gap={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Finish My company to unlock the workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="my-company-progress-count">
            {completeCount} of {checks.length} sections complete
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The report base year is the earliest year on the transactions you add. Complete every
          section below, then the rest of the workspace opens.
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{ height: 8, borderRadius: 1, mb: 1.5 }}
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="My company setup progress"
        />

        <List dense disablePadding>
          {checks.map((page) => {
            const status = myCompanyPageProgressStatus(typedProfile, page.id);
            const label = statusLabel(status);
            return (
              <ListItem key={page.id} disablePadding sx={{ py: 0.15 }}>
                <ListItemButton
                  component={RouterLink}
                  to={page.path}
                  sx={{ borderRadius: 1 }}
                  aria-label={`${page.label}: ${label}`}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {status === 'complete' ? (
                      <CheckCircleOutline color="success" fontSize="small" aria-hidden />
                    ) : status === 'in_progress' ? (
                      <TimelapseOutlined color="warning" fontSize="small" aria-hidden />
                    ) : (
                      <RadioButtonUnchecked color="action" fontSize="small" aria-hidden />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={page.label}
                    secondary={
                      status === 'complete'
                        ? 'Complete'
                        : status === 'in_progress'
                          ? `In progress — ${page.missing[0] || 'Finish and save'}`
                          : `Not started — ${page.missing[0] || 'Open to begin'}`
                    }
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: status === 'complete' ? 500 : 600,
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {nextIncomplete ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              component={RouterLink}
              to={nextIncomplete.path}
              size="small"
              variant="contained"
              data-testid="my-company-continue-setup"
            >
              Continue with {nextIncomplete.label}
            </Button>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default MyCompanyRequiredNotice;
